import { ref } from 'vue'

import { useMutation } from '@tanstack/vue-query'

import type {
  IChatBackgroundJobStartedEvent,
  IChatProcessingMetaEvent,
  IChatStreamEvent,
  IChatStreamInterruptedEvent,
} from '@/composables/useEventStream'
import { useEventStream } from '@/composables/useEventStream'
import { useMetricEvents } from '@/composables/useMetricEvents'
import { queryClient } from '@/lib/queryClient'
import { useChatStore } from '@/store/chat'
import { useFileStore } from '@/store/file'
import { useModelStore } from '@/store/model'
import { useToast } from '@/store/toast'

import { normalizeStreamError } from '..'
import { buildPlaceholderChat } from './chatPlaceholderChat'
import {
  createChatStreamActivityHandlers,
  createStreamState,
  handleInterruptedStream,
  handleMessageChunk,
  handleMessageCompleteChunk,
  handleNewTitleStreamEvent,
  handleReasoningChunk,
  handleStreamProgress,
  resetStreamMutationRefs,
  setupStreamPromiseHandling,
  stopThinkingAndResponding,
  streamMutationCallbacks,
} from './chatStreamHandlers'

import type { CreateChatData } from './chatQueryTypes'
import type { IChat } from '../types/chat'
import type { Router } from 'vue-router'

/**
 * Factory for POST `/chats` with SSE: creates the chat, navigates (unless draft), streams the reply.
 * Registers {@link StreamState} on the chat store so the UI can stop the stream from the sidebar/topbar.
 *
 * Non-draft sends jump to the chat view straight away, on a placeholder chat, and
 * only swap to the real id once `chat_created` lands — the request is no longer
 * something the user waits through on the home page.
 *
 * @returns `create` mutation and reactive `streamState`
 */
export function useNewChatMutation(router: Router) {
  const { isStreaming, streamError, stopStream, streamState } =
    createStreamState()
  // Track the current chatId for this streamState so we can clean it up
  const currentChatId = ref<string | null>(null)
  /**
   * The optimistic chat this mutation put on screen, while it is still ours.
   * Held here rather than read back from the store so a failure path can't act
   * on a placeholder a later send has since replaced.
   */
  const placeholderChat = ref<IChat | null>(null)

  /**
   * Publishes `streamState` to the store once the abort handler exists, under both
   * the placeholder id (so the loading view's stop button works before the chat
   * exists) and the real `chatId`.
   */
  const storeStreamStateIfReady = () => {
    if (!stopStream.value) return
    const chatStore = useChatStore()
    if (placeholderChat.value) {
      chatStore.setNewChatStreamState(placeholderChat.value.id, streamState)
    }
    if (currentChatId.value) {
      chatStore.setNewChatStreamState(currentChatId.value, streamState)
    }
  }

  /**
   * Renders the sent message on the ChatLoading page right away. The placeholder
   * carries its own thinking / responding / stream state, so the loading view has
   * a working typing indicator and stop button while the chat is being created.
   */
  const showPlaceholderChat = (data: CreateChatData) => {
    const chatStore = useChatStore()
    const placeholder = buildPlaceholderChat(data)
    placeholderChat.value = placeholder
    chatStore.setPlaceholderChat(placeholder)
    chatStore.setChatIsThinking(placeholder.id, true)
    chatStore.setChatIsResponding(placeholder.id, true)
    router.push({ name: 'ChatLoading' })
  }

  /**
   * True once the holding route is the committed route. `router.push` resolves
   * asynchronously (`ChatLoading` is a lazy chunk), so this is deliberately
   * false while that navigation is still in flight — a chat that arrives that
   * early has nothing to replace, and pushing over an uncommitted navigation
   * leaves the same clean history.
   */
  const isShowingPlaceholderChat = () =>
    !!placeholderChat.value && router.currentRoute.value.name === 'ChatLoading'

  /** Drops the placeholder's store state; the ChatLoading page unmounts with it. */
  const discardPlaceholderChat = () => {
    if (!placeholderChat.value) return
    useChatStore().clearPlaceholderChat(placeholderChat.value.id)
    placeholderChat.value = null
  }

  /**
   * Creation failed before `chat_created`, so there is no chat to show. Send the
   * user back where they came from — the error toast is raised by the caller.
   */
  const dismissPlaceholderChat = () => {
    const wasShowing = isShowingPlaceholderChat()
    const gptUuid = placeholderChat.value?.gpt_id
    discardPlaceholderChat()
    // Not on screen yet: the in-flight navigation lands on a ChatLoading with
    // no placeholder left, and its own guard sends the user back.
    if (!wasShowing) return
    router.replace({
      name: 'NewChat',
      query: gptUuid ? { gptUuid } : {},
    })
  }

  const create = useMutation<IChat, Error, CreateChatData>({
    mutationFn: (data: CreateChatData): Promise<IChat> => {
      return new Promise<IChat>((resolve, reject) => {
        resetStreamMutationRefs(isStreaming, streamError, stopStream)

        // Track if chat was created to avoid resolving/rejecting twice
        let chatCreated = false
        // Track chat ID for stopping the stream
        let chatId: string | null = null
        // `message_complete` is the only happy-path ending; anything else is a cut.
        let messageCompleted = false
        // Set when the server named the interruption, so EOF doesn't warn twice.
        let interruptionNotified = false

        /** Server closed the body mid-answer: no completion, no error, no user stop. */
        const wasCutShort = (wasAborted: boolean) =>
          !messageCompleted &&
          !interruptionNotified &&
          !wasAborted &&
          !streamError.value

        const payload = {
          ...data,
        }

        // Draft chats (GPT preview) stay in place; everything else jumps to the
        // chat view now and reconciles on `chat_created`. Built before the file
        // store is cleared so the placeholder can show the attachments.
        if (!data.is_draft) {
          showPlaceholderChat(data)
        }

        // Clear file store
        useFileStore().clearUploadedFiles()

        const startedJobs: IChatBackgroundJobStartedEvent[] = []
        const eventStream = useEventStream('/chats', {
          body: payload,
          method: 'POST',
          persistAcrossNavigation: true,
          eventHandlers: {
            chat_created: (chat: IChat) => {
              chatCreated = true
              chatId = chat.id
              currentChatId.value = chat.id
              useMetricEvents().sendEvent('new_conversation', {
                conversation_id: chat.id,
                model_id: data.model ?? data.gpt_object?.model ?? '',
              })
              // Handle new chat
              useChatStore().addNewChatToList(chat)
              useChatStore().setChat(chat)
              // `doGenerateImage` is client-only, so the server's chat object
              // drops it — put it back before the swap repaints.
              if (data.generate_image) {
                useChatStore().markLatestUserDoGenerateImage(chat.id)
              }
              // The model was picked on the new-chat surface, which has no chat
              // id to pin it to; do it now that the server has named one. GPT
              // chats send no model — theirs is pinned by the GPT.
              if (data.model) {
                useModelStore().rememberChatModel(chat.id, data.model)
              }
              storeStreamStateIfReady()
              if (!data.is_draft) {
                const chatRoute = { name: 'Chat', params: { id: chat.id } }
                // Replace, not push, so the placeholder route doesn't sit in
                // history between the home page and the chat.
                if (isShowingPlaceholderChat()) {
                  router.replace(chatRoute)
                } else {
                  router.push(chatRoute)
                }
              }
              discardPlaceholderChat()
              useChatStore().setChatIsThinking(chat.id, true)
              useChatStore().setChatStreamPhase(chat.id, 'request_received')
              useChatStore().setChatIsResponding(chat.id, true)

              // Resolve the mutation promise when chat is created
              resolve(chat)
            },
            message_chunk: (chunkData: IChatStreamEvent) => {
              handleMessageChunk(chunkData, true)
            },
            reasoning_chunk: handleReasoningChunk,
            stream_progress: handleStreamProgress,
            ...createChatStreamActivityHandlers(startedJobs),
            processing_meta: (payload: IChatProcessingMetaEvent) => {
              useChatStore().appendProcessingMeta(payload)
            },
            new_title: handleNewTitleStreamEvent,
            message_complete: (chunkData: IChatStreamEvent) => {
              messageCompleted = true
              handleMessageCompleteChunk(
                chunkData,
                () => {
                  const store = useChatStore()
                  store.removeNewChatFromList(chunkData.chat_id)
                  if (data.gpt_id) {
                    queryClient.invalidateQueries({
                      queryKey: ['gpts', 'infinite'],
                    })
                  }
                },
                startedJobs,
              )
            },
            stream_interrupted: (payload: IChatStreamInterruptedEvent) => {
              interruptionNotified = true
              const interruptedChatId = payload?.chat_id ?? chatId
              if (interruptedChatId) {
                handleInterruptedStream(interruptedChatId, payload?.message)
              }
            },
            error: (err) => {
              if (chatId) {
                stopThinkingAndResponding(chatId)
              }
              const error = normalizeStreamError(err)
              if (chatId) {
                ;(
                  error as Error & { streamAffectedChatId?: string }
                ).streamAffectedChatId = chatId
              }
              streamError.value = error
              useToast().onError(error.message)
              reject(error)
            },
          },
        })

        // Update stream state reactively
        isStreaming.value = true
        streamError.value = null

        // Create the stop function that will be stored in stopStream.value
        const stopFunction = () => {
          eventStream.stop()
          isStreaming.value = false
          if (chatId) {
            stopThinkingAndResponding(chatId)
          }
          if (currentChatId.value) {
            useChatStore().setNewChatStreamState(currentChatId.value, null)
          }
          // Stopped from the loading view, before the chat exists: nothing to
          // navigate to, so return to where the message was sent from.
          dismissPlaceholderChat()
        }

        stopStream.value = stopFunction

        // Update the streamState.stop function to directly call stopFunction
        // This ensures it works even when stored in Pinia
        streamState.stop = stopFunction

        // Now that stopStream.value is set, store streamState in Pinia if chat was already created
        storeStreamStateIfReady()

        // Setup stream promise handling
        setupStreamPromiseHandling<IChat>(
          eventStream,
          isStreaming,
          streamError,
          resolve,
          reject,
          (wasAborted) => {
            // If stream completed but chat wasn't created, that's an error
            if (!chatCreated) {
              const error = new Error('Chat was not created')
              streamError.value = error
              reject(error)
              return
            }
            if (!chatId) return
            if (wasCutShort(wasAborted)) {
              handleInterruptedStream(chatId)
              return
            }
            useChatStore().flushAssistantStreamBuffer(chatId)
          },
        )
      }).catch((error: Error) => {
        // Every failure funnels here — SSE `error` event, a non-OK POST, or a
        // stream that ended without `chat_created` — so the optimistic chat is
        // rolled back once, whichever way creation broke. Errors raised after
        // `chat_created` resolved the promise can't reach this, and by then
        // there is no placeholder left anyway.
        dismissPlaceholderChat()
        throw error
      })
    },
    ...streamMutationCallbacks(isStreaming, streamError, stopStream, {
      invalidateChatsQuery: true,
    }),
  })

  return {
    create,
    streamState,
  }
}
